/**
 * Plugin Configuration Utility Functions
 * Pure functions for plugin configuration management
 */

import { isLocalEnvironment } from '../config/environment';
import { resolveEnvValue } from './env';

export type PluginConfig<TConfig = Record<string, any>> = {
    storeId?: string;
    accountId?: string;
    basePath?: string;
    config?: any;
    staticResources?: Record<string, any>; // NEW: Static resources for local dev
    // Common config properties that plugins expect
    productId?: string;
    variants?: Record<string, string>;
    prices?: Record<string, any>;
    defaultCurrency?: string;
    hidePriceId?: string;
    upsellId?: string;
    googleApiKey?: string;
} & TConfig;

export type RawPluginConfig<TConfig = Record<string, any>> = {
    storeId?: string;
    accountId?: string;
    basePath?: string;
    config?: any;
} & TConfig;

/**
 * Load local development configuration
 */
const loadLocalDevConfig = async (configVariant = 'default'): Promise<PluginConfig | null> => {
    try {
        // Check for explicit environment override
        const env = resolveEnvValue('TAGADA_ENV') || resolveEnvValue('TAGADA_ENVIRONMENT');
        if (env === 'production') {
            // Skip local config loading if explicitly in production mode
            return null;
        }

        // Only try to load local config in TRUE local development (not deployed CDN instances)
        // Exclude CDN subdomains (e.g., instance-id.cdn.localhost)
        if (!isLocalEnvironment(true)) {
            return null;
        }

        // Load local store/account config
        const localResponse = await fetch('/.local.json');
        if (!localResponse.ok) {
            return null;
        }
        const localConfig = await localResponse.json();

        // Load deployment config (specified variant or fallback to default)
        let config: Record<string, any> = {};
        let configLoaded = false;

        try {
            // Try .tgd.json first (new format), then fallback to .json
            let deploymentResponse = await fetch(`/config/${configVariant}.tgd.json`);
            if (!deploymentResponse.ok) {
                deploymentResponse = await fetch(`/config/${configVariant}.json`);
            }

            if (deploymentResponse.ok) {
                config = await deploymentResponse.json();
                configLoaded = true;
            }
        } catch {
            // Config fetch failed, will try fallback
        }

        // If config didn't load and it's not 'default', try fallback to default
        if (!configLoaded && configVariant !== 'default') {
            console.warn(`⚠️ Config variant '${configVariant}' not found, falling back to 'default'`);
            try {
                let defaultResponse = await fetch('/config/default.tgd.json');
                if (!defaultResponse.ok) {
                    defaultResponse = await fetch('/config/default.json');
                }

                if (defaultResponse.ok) {
                    config = await defaultResponse.json();
                    configLoaded = true;
                    console.log(`✅ Fallback to 'default' config successful`);
                }
            } catch {
                // Default config also failed
            }
        }

        // Final warning if no config was loaded
        if (!configLoaded) {
            if (configVariant === 'default') {
                console.warn(`⚠️ No 'default' config found. Create /config/default.tgd.json`);
            } else {
                console.warn(`⚠️ Neither '${configVariant}' nor 'default' config found. Create /config/default.tgd.json`);
            }
        }

        const result = {
            storeId: localConfig.storeId,
            accountId: localConfig.accountId,
            basePath: localConfig.basePath,
            config,
        };

        console.log('🛠️ Using local development plugin config:', result);

        return result;
    } catch {
        return null;
    }
};

/**
 * Load static resources for local development
 * 
 * Priority:
 * 1. /config/funnel.local.json (NEW - recommended)
 * 2. /config/resources.static.json (OLD - deprecated, for backward compatibility)
 */
const loadStaticResources = async (): Promise<Record<string, any> | null> => {
    try {
        // Check for explicit environment override
        const env = resolveEnvValue('TAGADA_ENV') || resolveEnvValue('TAGADA_ENVIRONMENT');
        if (env === 'production') {
            return null;
        }

        // Only try to load in TRUE local development (not deployed CDN instances)
        // Exclude CDN subdomains (e.g., instance-id.cdn.localhost)
        if (!isLocalEnvironment(true)) {
            return null;
        }

        // ============================================================
        // PRIORITY 1: NEW format - funnel.local.json
        // Also populates the stepConfig cache for getAssignedStepConfig()
        // ============================================================
        try {
            console.log('🛠️ [V2] Attempting to load /config/funnel.local.json...');
            const funnelResponse = await fetch('/config/funnel.local.json');
            if (funnelResponse.ok) {
                const funnelConfig = await funnelResponse.json();
                console.log('🛠️ [V2] ✅ Loaded local funnel config (NEW format):', funnelConfig);
                
                // Also trigger loadLocalFunnelConfig to populate the cache
                // Import dynamically to avoid circular dependency
                const { loadLocalFunnelConfig } = await import('../funnelClient');
                await loadLocalFunnelConfig();
                
                // Return staticResources in the old format for backward compatibility
                // The new stepConfig system reads from funnel.local.json via getAssignedStepConfig()
                if (funnelConfig.staticResources) {
                    // Transform flat format to old nested format for legacy compatibility
                    const transformed: Record<string, any> = {};
                    for (const [key, value] of Object.entries(funnelConfig.staticResources)) {
                        transformed[key] = { id: value };
                    }
                    return transformed;
                }
                return null;
            }
        } catch (e) {
            console.log('🛠️ [V2] funnel.local.json not found, trying legacy format...');
        }

        // ============================================================
        // PRIORITY 2: OLD format - resources.static.json (deprecated)
        // ============================================================
        console.log('🛠️ [V2] Attempting to load /config/resources.static.json (legacy)...');
        const response = await fetch('/config/resources.static.json');
        if (!response.ok) {
            console.log('🛠️ [V2] No local static resources found');
            return null;
        }

        const staticResources = await response.json();
        console.log('🛠️ [V2] ✅ Loaded legacy static resources:', staticResources);
        return staticResources;
    } catch (error) {
        console.error('🛠️ [V2] ❌ Error loading static resources:', error);
        return null;
    }
};

/**
 * Helper to get content from meta tag
 */
const getMetaContent = (name: string): string | undefined => {
    if (typeof document === 'undefined') return undefined;
    const metaTag = document.querySelector(`meta[name="${name}"]`);
    return metaTag?.getAttribute('content') || undefined;
};

/**
 * Read config from window global (preferred) or fall back to x-plugin-config meta tag
 * (retrocompat with old deployments that still inject the meta tag).
 */
const readWindowConfig = (): Record<string, any> => {
    if (typeof window !== 'undefined' && (window as any).__TAGADA_PLUGIN_CONFIG__) {
        return (window as any).__TAGADA_PLUGIN_CONFIG__;
    }
    try {
        const encoded = getMetaContent('x-plugin-config');
        if (encoded) return JSON.parse(decodeURIComponent(encoded));
    } catch { /* ignore */ }
    return {};
};

/**
 * Synchronously get plugin config from meta tags
 * This is a lightweight sync alternative to loadPluginConfig
 * Use this when you need immediate config access (e.g., in CDN bundles)
 * 
 * @returns Plugin config from meta tags or defaults
 */
export function getPluginConfig(): PluginConfig {
    if (typeof document === 'undefined') {
        return { basePath: '/', config: {} };
    }

    const storeId = getMetaContent('x-plugin-store-id');
    const accountId = getMetaContent('x-plugin-account-id');
    const basePath = getMetaContent('x-plugin-base-path') || '/';

    const config = readWindowConfig();

    return { storeId, accountId, basePath, config };
}

/**
 * Load production config from meta tags (HIGHEST PRIORITY)
 * Meta tags are injected by the plugin middleware during HTML serving
 * This avoids making an additional HEAD request (~300ms savings)
 * 
 * Returns null if meta tags are not present (not in production deployment)
 */
const loadProductionConfig = async (): Promise<PluginConfig | null> => {
    try {
        // Check if we're in a browser environment
        if (typeof document === 'undefined') {
            return null;
        }

        // Read store/account info from meta tags (injected by middleware)
        const storeId = getMetaContent('x-plugin-store-id');
        const accountId = getMetaContent('x-plugin-account-id');

        // If no store ID in meta tags, this is not a production deployment with injected config
        if (!storeId) {
            return null;
        }

        const basePath = getMetaContent('x-plugin-base-path') || '/';

        const config = readWindowConfig();

        // Final validation
        if (!accountId) {
            console.warn('⚠️ Plugin config: Account ID not found in meta tags');
        }

        const result = { storeId, accountId, basePath, config };
        console.log('🚀 [HIGHEST PRIORITY] Plugin config loaded from meta tags (runtime injected config):', {
            storeId,
            accountId,
            basePath,
            configKeys: Object.keys(config),
            configSize: JSON.stringify(config).length
        });

        return result;
    } catch (error) {
        console.warn('⚠️ Error loading production config from meta tags:', error);
        return null;
    }
};

/**
 * Core plugin config loading function
 * 
 * PRIORITY ORDER (highest to lowest):
 * 1. Injected meta tags (runtime config) - ALWAYS CHECK FIRST
 * 2. Raw config parameter (programmatic override)
 * 3. Environment variables (build-time config)
 * 4. Local dev files (.local.json + config files)
 * 5. Defaults
 */
export const loadPluginConfig = async (
    configVariant = 'default',
    rawConfig?: RawPluginConfig,
): Promise<PluginConfig> => {
    console.log('🔧 [V2] loadPluginConfig called with variant:', configVariant);

    // Load static resources first (only in local dev)
    const staticResources = await loadStaticResources();

    // ============================================================
    // PRIORITY 1: Check meta tags FIRST (runtime injected config)
    // This ensures deployed plugins always use injected config
    // ============================================================
    const productionConfig = await loadProductionConfig();
    if (productionConfig) {
        const result = {
            ...productionConfig,
            staticResources: staticResources ?? undefined,
        };
        console.log('✅ [V2] Using INJECTED config from meta tags (HIGHEST PRIORITY)');
        return result;
    }

    // ============================================================
    // PRIORITY 2: Raw config parameter (programmatic override)
    // ============================================================
    if (rawConfig) {
        const result = {
            storeId: rawConfig.storeId,
            accountId: rawConfig.accountId,
            basePath: rawConfig.basePath ?? '/',
            config: rawConfig.config ?? {},
            staticResources: staticResources ?? undefined,
        };
        console.log('✅ [V2] Using raw config parameter (PRIORITY 2)');
        return result;
    }

    // ============================================================
    // PRIORITY 3: Environment variables (build-time config)
    // Only in localhost - production should use meta tags
    // ============================================================
    const envConfig = await createRawPluginConfig();
    if (envConfig) {
        const result = {
            ...envConfig,
            staticResources: staticResources ?? undefined,
        };
        console.log('✅ [V2] Using environment variables config (PRIORITY 3 - build time)');
        return result;
    }

    // ============================================================
    // PRIORITY 4: Local development config files
    // ============================================================
    const localConfig = await loadLocalDevConfig(configVariant);
    if (localConfig) {
        const result = {
            ...localConfig,
            staticResources: staticResources ?? undefined,
        };
        console.log('✅ [V2] Using local dev config files (PRIORITY 4)');
        return result;
    }

    // ============================================================
    // PRIORITY 5: Defaults (no config found)
    // ============================================================
    console.warn('⚠️ [V2] No plugin config found - using defaults');
    return {
        basePath: '/',
        config: {},
        staticResources: staticResources ?? undefined,
    };
};
/**
 * Helper to load local config file for development (from /config directory)
 * Returns the config object that can be passed to TagadaProvider
 */
export async function loadLocalConfig(configName: string = 'default', defaultConfig?: any): Promise<Record<string, unknown> | null> {
    try {
        // Only load in localhost
        if (!isLocalEnvironment()) {
            return null;
        }

        if (defaultConfig) {
            return defaultConfig;
        }

        // Try .tgd.json first, then .json, then .config.json
        const possiblePaths = [
            `/config/${configName}.config.json`,
            `/config/${configName}.tgd.json`,
            `/config/${configName}.json`,
        ];

        for (const path of possiblePaths) {
            try {
                const response = await fetch(path);
                if (response.ok) {
                    const config = await response.json() as Record<string, unknown>;

                    return config;
                }
            } catch {
                continue;
            }
        }


        console.warn(`⚠️ [loadLocalConfig] No config found for '${configName}'`);
        return null;
    } catch (error) {
        console.error('[loadLocalConfig] Error loading config:', error);
        return null;
    }
}
/**
 * Creates a RawPluginConfig from environment variables (build-time config)
 * 
 * This should ONLY be used when:
 * 1. Running in localhost development
 * 2. Meta tags are not present (not a deployed plugin)
 * 
 * @returns A RawPluginConfig object or undefined if not applicable
 */
export async function createRawPluginConfig(): Promise<RawPluginConfig | undefined> {
    try {
        // Only run in true localhost - production MUST use meta tags
        if (!isLocalEnvironment()) {
            return undefined;
        }

        const storeId = resolveEnvValue('TAGADA_STORE_ID');
        const accountId = resolveEnvValue('TAGADA_ACCOUNT_ID');
        const basePath = resolveEnvValue('TAGADA_BASE_PATH');
        const configName = resolveEnvValue('TAGADA_CONFIG_NAME');

        if (!storeId || !accountId) {
            return undefined;
        }

        const resolvedConfig = await loadLocalConfig(configName);

        if (!resolvedConfig) {
            return undefined;
        }

        const rawConfig: RawPluginConfig = {
            storeId: storeId,
            accountId: accountId,
            basePath: basePath || '/',
            config: resolvedConfig,
        };

        console.log('🛠️ [createRawPluginConfig] Using environment variables (build-time config):', {
            hasStoreId: !!rawConfig.storeId,
            hasAccountId: !!rawConfig.accountId,
            basePath: rawConfig.basePath,
            hasConfig: !!rawConfig.config,
        });

        return rawConfig;
    } catch (error) {
        console.error('[createRawPluginConfig] Error creating config:', error);
        return undefined;
    }
}

export class PluginConfigUtils {
    /**
     * Get plugin configuration from various sources
     */
    static getPluginConfig<TConfig = Record<string, any>>(
        rawConfig?: RawPluginConfig<TConfig>,
        context?: {
            storeId?: string;
            accountId?: string;
            basePath?: string;
        }
    ): PluginConfig<TConfig> {
        // Priority: rawConfig > context > defaults
        return {
            storeId: rawConfig?.storeId || context?.storeId,
            accountId: rawConfig?.accountId || context?.accountId,
            basePath: rawConfig?.basePath || context?.basePath || '/',
            config: rawConfig?.config || ({} as TConfig),
        } as PluginConfig<TConfig>;
    }

    /**
     * Validate plugin configuration
     */
    static validateConfig(config: PluginConfig): boolean {
        return !!(config.storeId && config.accountId);
    }
}
